import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type Primitive = string | number | boolean;
type FilterValue = Primitive | Primitive[] | null | undefined;
type FilterShape = Record<string, FilterValue>;

interface UseFilterUrlStateOptions<F extends FilterShape> {
  /** Default values; URL params override these on mount. */
  initial: F;
  /**
   * Subset of keys whose URL value should be coerced to number.
   * Strings and booleans are coerced automatically; arrays must be
   * declared explicitly.
   */
  numericKeys?: (keyof F)[];
  arrayKeys?: (keyof F)[];
  /**
   * Reserved param names not part of the filter shape. Defaults to
   * `['page', 'pageSize']` so list-page hooks can store pagination
   * alongside filters without round-tripping it through here.
   */
  reservedKeys?: string[];
}

/**
 * Two-way sync between a flat filter object and `?key=value` URL params.
 * URL is the source of truth; setting filters pushes a new history entry.
 *
 * Empty strings, null, and undefined are omitted from the URL.
 * Booleans round-trip as 'true' / 'false'.
 * Arrays serialize as comma-separated values.
 */
export function useFilterUrlState<F extends FilterShape>({
  initial,
  numericKeys = [],
  arrayKeys = [],
  reservedKeys = ['page', 'pageSize'],
}: UseFilterUrlStateOptions<F>): [
  F,
  (updates: Partial<F>, extra?: (out: URLSearchParams) => void) => void,
  (extra?: (out: URLSearchParams) => void) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();

  const numericSet = useMemo(() => new Set(numericKeys as string[]), [numericKeys]);
  const arraySet = useMemo(() => new Set(arrayKeys as string[]), [arrayKeys]);
  const reservedSet = useMemo(() => new Set(reservedKeys), [reservedKeys]);

  const parseFromUrl = useCallback((): F => {
    const out: FilterShape = { ...initial };
    for (const key of Object.keys(initial)) {
      const raw = searchParams.get(key);
      if (raw == null) continue;
      if (arraySet.has(key)) {
        out[key] = raw.split(',').filter(Boolean);
      } else if (numericSet.has(key)) {
        const n = Number(raw);
        out[key] = Number.isFinite(n) ? n : initial[key];
      } else if (raw === 'true') {
        out[key] = true;
      } else if (raw === 'false') {
        out[key] = false;
      } else {
        out[key] = raw;
      }
    }
    return out as F;
  }, [searchParams, initial, numericSet, arraySet]);

  const [filters, setFilters] = useState<F>(parseFromUrl);

  // Re-parse when URL changes (back/forward navigation).
  const lastParamsKey = useRef(searchParams.toString());
  useEffect(() => {
    const key = searchParams.toString();
    if (key === lastParamsKey.current) return;
    lastParamsKey.current = key;
    setFilters(parseFromUrl());
  }, [searchParams, parseFromUrl]);

  const writeToUrl = useCallback(
    (next: F, extra?: (out: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const out = new URLSearchParams();
        // Preserve reserved (page/pageSize)
        prev.forEach((value, key) => {
          if (reservedSet.has(key)) out.set(key, value);
        });
        for (const [key, value] of Object.entries(next)) {
          if (value == null) continue;
          if (Array.isArray(value)) {
            if (value.length > 0) out.set(key, value.join(','));
            continue;
          }
          if (typeof value === 'string' && value === '') continue;
          // Convention: falsy booleans = "filter off", omit from URL.
          if (typeof value === 'boolean' && !value) continue;
          out.set(key, String(value));
        }
        // Caller (e.g. useListPage) gets to mutate reserved params in the
        // same write so we don't race two setSearchParams calls.
        extra?.(out);
        return out;
      }, { replace: true });
      lastParamsKey.current = '__pending__';
    },
    [setSearchParams, reservedSet],
  );

  const update = useCallback(
    (updates: Partial<F>, extra?: (out: URLSearchParams) => void) => {
      setFilters((prev) => {
        const next = { ...prev, ...updates };
        writeToUrl(next, extra);
        return next;
      });
    },
    [writeToUrl],
  );

  const reset = useCallback(
    (extra?: (out: URLSearchParams) => void) => {
      setFilters(initial);
      writeToUrl(initial, extra);
    },
    [initial, writeToUrl],
  );

  return [filters, update, reset];
}
